import { useQuery } from 'react-query';
import { useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { TRADE } from '../../constants/queryDefinition';
import {
  initialState as tradeDetailsInitialState,
  tradeDetailsSlice
} from '../../../redux/slices/tradeDetails';
import ReducerConstants from '../../constants/reducers';
import TradeDetails from '../../models/TradeDetails';
import TradeService from '../../../services/TradeService';
import useManufacturerList from '../vehicle/useManufacturerList';
import { organizationSlice } from '../../../redux/slices/organization';
import useOrganizationDealer from '../useOrganizationDealer';
import { TradeViewVariant } from '../../types/trade';

const useTradeDetails = (
  tradeId?: number,
  isOMP?: false,
  handleOmpBidding?: (biddingData: any) => void
) => {
  const dispatch = useDispatch();
  const tradeService = TradeService.get();

  // Get latest items from the Redux store
  const tradeDetails = useSelector(
    state => state[ReducerConstants.TRADE_DETAILS],
    shallowEqual
  );
  const dealer = useSelector(
    state => state[ReducerConstants.ORGANIZATION_DEALER],
    shallowEqual
  );
  const user = useSelector(
    state => state[ReducerConstants.AUTH]?.user,
    shallowEqual
  );
  const configurations = useSelector(
    state => state[ReducerConstants.AUTH]?.system?.configurations,
    shallowEqual
  );

  const liveBid = useSelector(
    state => state[ReducerConstants.LIVE_BIDS]?.[`${tradeId}`],
    shallowEqual
  );

  const { data: manufacturers }: any = useManufacturerList();

  const { companyId, country } = user || {};

  const { data: tradeDetailsData, isFetching } = useQuery(
    [TRADE.DETAILS, tradeId],
    () => tradeService.getTradeDetails(tradeId),
    {
      enabled: tradeId !== undefined,
      cacheTime: 0,
      retry: 5,
      placeholderData: {},
      onSuccess: data => {
        if (
          tradeDetails === tradeDetailsInitialState ||
          Number(tradeDetails?.id) !== Number(tradeId) ||
          (isOMP && data)
        ) {
          dispatch(tradeDetailsSlice.actions.setTradeDetails(data));
          // Communicates bidding information to vehicle card if on side rail VDP
          if (handleOmpBidding) {
            const details = data?.details;
            handleOmpBidding({
              tradeId,
              auctionSummary: {
                ...data?.auctionSummary,
                activeExpiryTime: details?.activeExpiryTime,
                tradeFloorPrice: details?.tradeFloorPrice,
                upcomingProxyAmount: details?.upcomingProxy?.proxyAmount,
                upcomingBidAmount: details?.upcomingProxy?.bidAmount
              },
              source: TradeViewVariant.VDP
            });
          }
        }
      }
    }
  );

  const { data: organizationDealerResponse } = useOrganizationDealer(companyId);

  useEffect(() => {
    if (organizationDealerResponse) {
      dispatch(
        organizationSlice.actions.setOrganizationDealerDetails(
          organizationDealerResponse
        )
      );
    }
  }, [dispatch, organizationDealerResponse]);

  return new TradeDetails({
    configurations,
    countryId: country?.id,
    dealer,
    isFetching,
    liveBid,
    manufacturers,
    trade: tradeDetailsData,
    user
  });
};

export default useTradeDetails;
